import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  Chip,
  Stack,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import KeyRoundedIcon from "@mui/icons-material/KeyRounded";
import TerminalRoundedIcon from "@mui/icons-material/TerminalRounded";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 4,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
  },
};

export default function Credentials({
  value,
  onChange,
  showCurl,
  curlCommand,
  title,
  mqttxCommand,
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedMQTTX, setCopiedMQTTX] = useState(false);

  const isTls = String(value.security_mode || "PLAINTEXT").toUpperCase() === "TLS";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(curlCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Clipboard copy failed", err);
    }
  };

  const handleCopyMQTTX = async () => {
    try {
      await navigator.clipboard.writeText(mqttxCommand);
      setCopiedMQTTX(true);
      setTimeout(() => setCopiedMQTTX(false), 2000);
    } catch (err) {
      console.error("Clipboard copy failed", err);
    }
  };

  const handleChange = (field, val) => {
    onChange({
      ...value,
      [field]: val,
    });
  };

  return (
    <Box
      sx={{
        borderRadius: 5,
        border: "1px solid rgba(226, 232, 240, 0.95)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))",
        p: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={1.25} sx={{ mb: 3 }}>
        <Chip
          label="Step 3"
          sx={{
            width: "fit-content",
            color: "#7C3AED",
            backgroundColor: "rgba(124,58,237,0.12)",
            fontWeight: 700,
          }}
        />
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0F172A" }}>
          Credentials & commands
        </Typography>
        <Typography sx={{ color: "#64748B", lineHeight: 1.7 }}>
          Review the provisioned connection profile and copy the generated
          commands for validation or integration testing.
        </Typography>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2.5,
        }}
      >
        <Field label="Device ID">
          <TextField
            fullWidth
            placeholder="Device id"
            value={value.device_id}
            onChange={(e) => handleChange("device_id", e.target.value)}
            sx={fieldSx}
          />
        </Field>

        <Field label="Broker">
          <TextField
            fullWidth
            placeholder="Broker"
            value={value.broker_id}
            onChange={(e) => handleChange("broker_id", e.target.value)}
            sx={fieldSx}
          />
        </Field>

        <Field label="Security Mode">
          <TextField
            fullWidth
            placeholder="Security mode"
            value={value.security_mode || "PLAINTEXT"}
            onChange={(e) => handleChange("security_mode", e.target.value)}
            sx={fieldSx}
          />
        </Field>

        <Field label="Port">
          <TextField
            fullWidth
            placeholder="Port"
            value={value.port || ""}
            onChange={(e) => handleChange("port", e.target.value)}
            sx={fieldSx}
          />
        </Field>

        <Field label={isTls ? "Username / Identity" : "Username"}>
          <TextField
            fullWidth
            placeholder={isTls ? "Certificate identity" : "Username"}
            value={value.username}
            onChange={(e) => handleChange("username", e.target.value)}
            sx={fieldSx}
          />
        </Field>

        {isTls ? (
          <Field label="Certificate Identity">
            <TextField
              fullWidth
              placeholder="Certificate identity"
              value={value.client_cert_identity || value.username || ""}
              onChange={(e) => handleChange("client_cert_identity", e.target.value)}
              sx={fieldSx}
            />
          </Field>
        ) : (
          <Field label="Password">
            <TextField
              fullWidth
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              value={value.password}
              onChange={(e) => handleChange("password", e.target.value)}
              sx={fieldSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Field>
        )}

        {isTls && (
          <>
            <Field label="CA Certificate">
              <TextField
                fullWidth
                placeholder="CA certificate filename"
                value={value.ca_cert_filename || ""}
                onChange={(e) => handleChange("ca_cert_filename", e.target.value)}
                sx={fieldSx}
              />
            </Field>

            <Field label="Device Certificate">
              <TextField
                fullWidth
                placeholder="Device certificate filename"
                value={value.client_cert_filename || ""}
                onChange={(e) =>
                  handleChange("client_cert_filename", e.target.value)
                }
                sx={fieldSx}
              />
            </Field>

            <Field label="Device Key">
              <TextField
                fullWidth
                placeholder="Device key filename"
                value={value.client_key_filename || ""}
                onChange={(e) => handleChange("client_key_filename", e.target.value)}
                sx={fieldSx}
              />
            </Field>
          </>
        )}
      </Box>

      {isTls && (
        <Box
          sx={{
            mt: 2.5,
            p: 2,
            borderRadius: 3,
            border: "1px solid rgba(14, 165, 233, 0.18)",
            background:
              "linear-gradient(135deg, rgba(224,242,254,0.72), rgba(240,249,255,0.82))",
          }}
        >
          <Typography sx={{ color: "#0C4A6E", fontWeight: 700 }}>
            TLS devices authenticate with certificates on port {value.port || 8883}.
          </Typography>
          <Typography sx={{ mt: 0.75, color: "#075985", fontSize: 14 }}>
            The generated MQTTX command expects `{value.ca_cert_filename || "ca.crt"}`, `{value.client_cert_filename || "device.crt"}`, and `{value.client_key_filename || "device.key"}` to be available locally.
          </Typography>
        </Box>
      )}

      {showCurl && (
        <Stack spacing={2} sx={{ mt: 3 }}>
          <CommandCard
            icon={<TerminalRoundedIcon />}
            title="cURL command"
            command={curlCommand}
            copied={copied}
            onCopy={handleCopy}
          />

          <CommandCard
            icon={<KeyRoundedIcon />}
            title={title}
            command={mqttxCommand}
            copied={copiedMQTTX}
            onCopy={handleCopyMQTTX}
          />
        </Stack>
      )}
    </Box>
  );
}

function Field({ label, children }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#334155", mb: 1 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function CommandCard({ title, command, copied, onCopy, icon }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 4,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        background: "linear-gradient(135deg, #0F172A, #0B1F33)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              display: "grid",
              placeItems: "center",
              borderRadius: 3,
              color: "#BAE6FD",
              backgroundColor: "rgba(255,255,255,0.08)",
            }}
          >
            {icon}
          </Box>
          <Typography sx={{ color: "#E2E8F0", fontWeight: 700 }}>{title}</Typography>
        </Stack>

        <Tooltip title={copied ? "Copied!" : "Copy"}>
          <IconButton size="small" onClick={onCopy} sx={{ color: "#E2E8F0" }}>
            {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          borderRadius: 3,
          overflowX: "auto",
          fontSize: 13,
          lineHeight: 1.7,
          color: "#E2E8F0",
          backgroundColor: "rgba(15,23,42,0.6)",
          border: "1px solid rgba(148,163,184,0.12)",
          fontFamily: "Consolas, Monaco, monospace",
        }}
      >
        {command}
      </Box>
    </Box>
  );
}
